import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  alpha,
} from '@mui/material';
import type { Node } from '@xyflow/react';
import {
  AccountCircle,
  Analytics,
  AssignmentTurnedIn,
  Badge,
  Flag,
  Hub,
  Layers,
  Person,
  RateReview,
  ShowChart,
  TouchApp,
  TrendingUp,
  ViewModule,
} from '@mui/icons-material';
import type { TechnicianJourney, CustomerJourney } from '../../types/technicianJourney';
import { mergeTechnicianPhases } from '../../services/journeyApi';
import type { JourneyHubNodeData, JourneyMetricNodeData, JourneyPhaseNodeData, JourneyPillarNodeData } from './buildJourneyFlow';

export type JourneyInspectorMode = 'technician' | 'customer';

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return String(v);
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function KVRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      className="uj-inspector-kv"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.1fr)',
        gap: 1.5,
        py: 1,
        px: 0.5,
        borderBottom: '1px solid',
        borderColor: (t) => alpha(t.palette.divider, 0.85),
        alignItems: 'start',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12.5, textAlign: 'right', wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function SectionTitle({ icon, title, children }: { icon: React.ReactNode; title?: React.ReactNode; children?: React.ReactNode }) {
  const text = title ?? children;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2.5, mb: 1 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 18 } }}>{icon}</Box>
      <Typography variant="overline" fontWeight={800} letterSpacing={0.8} sx={{ color: 'text.primary' }}>
        {text}
      </Typography>
    </Stack>
  );
}

function EmptyHint() {
  return (
    <Box
      className="uj-inspector-empty"
      sx={{
        py: 4,
        px: 2,
        textAlign: 'center',
        borderRadius: 2,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
      }}
    >
      <TouchApp sx={{ fontSize: 36, color: 'primary.main', opacity: 0.5, mb: 1 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Select a graph card
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, mx: 'auto', lineHeight: 1.5 }}>
        Click any node in the journey map to open its full details here for support review.
      </Typography>
    </Box>
  );
}

function typeChip(node: Node | null) {
  if (!node?.type) return null;
  const map: Record<string, string> = {
    journeyHub: 'Identity hub',
    journeyMetric: 'Metrics',
    journeyPhase: 'Phase',
    journeyPillar: 'Onboarding step',
  };
  return (
    <Chip size="small" label={map[node.type] ?? node.type} sx={{ fontWeight: 700, fontSize: 10 }} color="primary" variant="outlined" />
  );
}

function TechnicianInspectorBody({ node, j }: { node: Node; j: TechnicianJourney }) {
  const p = mergeTechnicianPhases(j.phases);
  const id = node.id;

  if (id === 'hub-identity') {
    const d = node.data as JourneyHubNodeData;
    return (
      <>
        <SectionTitle icon={<Hub />} title="Identity & registration" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.titleAr}
        </Typography>
        <Stack spacing={0}>
          <KVRow label="Technician ID" value={j.technicianId} />
          <KVRow label="Name" value={j.name} />
          <KVRow label="Phone" value={j.phoneNumber} />
          <KVRow label="Email" value={j.email} />
          <KVRow label="Status" value={<Chip size="small" label={j.status} />} />
          <KVRow label="Current phase" value={j.currentPhase} />
          <KVRow label="Overall progress" value={`${j.overallProgress}%`} />
          <KVRow label="Online" value={j.isOnline ? 'Yes' : 'No'} />
          <KVRow label="Last seen" value={j.lastSeen || '—'} />
          <KVRow label="City" value={j.city} />
          <KVRow label="Registration" value={j.registrationDate} />
          <KVRow label="Terms accepted" value={j.acceptedTermsAt} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Summary lines from graph
        </Typography>
        <Box component="ul" className="uj-inspector-bullets" sx={{ m: 0, pl: 2, mt: 0.5 }}>
          {d.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}>
                {line}
              </Typography>
            </li>
          ))}
        </Box>
      </>
    );
  }

  if (id === 'metric-activity' && j.activity) {
    const a = j.activity;
    return (
      <>
        <SectionTitle icon={<Analytics />} title="Activity" />
        <Stack spacing={0}>
          <KVRow label="Last login" value={a.lastLoginDate} />
          <KVRow label="Logins (30 days)" value={a.loginCountLast30Days} />
          <KVRow label="Days since activity" value={a.daysSinceLastActivity} />
        </Stack>
      </>
    );
  }

  if (id === 'metric-quotes' && j.quotes) {
    const q = j.quotes;
    return (
      <>
        <SectionTitle icon={<ShowChart />} title="Quotes" />
        <Stack spacing={0}>
          <KVRow label="Total quotes" value={q.totalQuotes} />
          <KVRow label="Last 7 days" value={q.quotesLast7Days} />
          <KVRow label="Last 30 days" value={q.quotesLast30Days} />
          <KVRow label="Last quote date" value={q.lastQuoteDate} />
          <KVRow label="Frequency" value={q.quoteFrequency} />
        </Stack>
      </>
    );
  }

  if (id === 'metric-performance' && j.performance) {
    const x = j.performance;
    return (
      <>
        <SectionTitle icon={<TrendingUp />} title="Performance" />
        <Stack spacing={0}>
          <KVRow label="Won projects" value={x.wonProjects} />
          <KVRow label="Completed" value={x.completedProjects} />
          <KVRow label="In progress" value={x.inProgressProjects} />
          <KVRow label="Cancelled" value={x.cancelledProjects} />
          <KVRow label="Win rate" value={fmtPct(x.winRate)} />
          <KVRow label="Completion rate" value={fmtPct(x.completionRate)} />
        </Stack>
      </>
    );
  }

  if (id === 'metric-quality' && j.quality) {
    const x = j.quality;
    return (
      <>
        <SectionTitle icon={<RateReview />} title="Quality" />
        <Stack spacing={0}>
          <KVRow label="Average rating" value={x.averageRating} />
          <KVRow label="Total reviews" value={x.totalReviews} />
          <KVRow label="Complaints" value={x.complaintsCount} />
          <KVRow label="Support tickets" value={x.supportTicketsCount} />
        </Stack>
      </>
    );
  }

  if (id === 'metric-engagement' && j.engagement) {
    const x = j.engagement;
    return (
      <>
        <SectionTitle icon={<Person />} title="Engagement" />
        <Stack spacing={0}>
          <KVRow label="Conversations" value={x.conversationsCount} />
          <KVRow label="Response rate" value={x.responseRate != null ? fmtPct(x.responseRate) : '—'} />
          <KVRow label="Avg response (min)" value={x.avgResponseTimeMinutes} />
        </Stack>
      </>
    );
  }

  if (id === 'metric-platform' && j.platform) {
    const x = j.platform;
    return (
      <>
        <SectionTitle icon={<ViewModule />} title="Platform" />
        <Stack spacing={0}>
          <KVRow label="Profile views" value={x.profileViews} />
          <KVRow label="Views (30 days)" value={x.profileViewsLast30Days} />
          <KVRow label="Unique viewers" value={x.uniqueViewers} />
        </Stack>
      </>
    );
  }

  if (id.startsWith('metric-')) {
    const d = node.data as JourneyMetricNodeData;
    return (
      <>
        <SectionTitle icon={<Layers />} title={d.label} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.labelAr}
        </Typography>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          API did not return structured data for this block. Showing card lines only.
        </Typography>
        <Box component="ul" className="uj-inspector-bullets" sx={{ m: 0, pl: 2 }}>
          {d.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                {line}
              </Typography>
            </li>
          ))}
        </Box>
      </>
    );
  }

  if (id === 'spine-signup') {
    const s = p.signup;
    return (
      <>
        <SectionTitle icon={<Flag />} title="Signup phase" />
        <Stack spacing={0}>
          <KVRow label="Completed" value={fmt(s.completed)} />
          <KVRow label="Status" value={s.status} />
          <KVRow label="OTP verified" value={fmt(s.otpVerified)} />
        </Stack>
      </>
    );
  }

  if (id === 'spine-profile') {
    const x = p.profileCompletion;
    return (
      <>
        <SectionTitle icon={<Badge />} title="Profile completion" />
        <Stack spacing={0}>
          <KVRow label="Completed" value={fmt(x.completed)} />
          <KVRow label="Profile complete" value={fmt(x.profileComplete)} />
          <KVRow label="Email" value={fmt(x.hasEmail)} />
          <KVRow label="Description" value={fmt(x.hasDescription)} />
          <KVRow label="Certificates" value={fmt(x.hasCertificates)} />
          <KVRow label="Regions" value={fmt(x.hasRegions)} />
          <KVRow label="Years of experience" value={fmt(x.hasYearsOfExperience)} />
          <KVRow label="Profile image" value={fmt(x.hasProfileImage)} />
          <KVRow label="National ID" value={fmt(x.hasNationalId)} />
        </Stack>
      </>
    );
  }

  if (id === 'spine-approval') {
    const x = p.adminApproval;
    return (
      <>
        <SectionTitle icon={<AssignmentTurnedIn />} title="Admin approval" />
        <Stack spacing={0}>
          <KVRow label="Completed" value={fmt(x.completed)} />
          <KVRow label="Pending data requests" value={x.pendingDataRequests} />
        </Stack>
        <Typography variant="overline" fontWeight={700} sx={{ mt: 2, display: 'block' }}>
          Data requests
        </Typography>
        <Table size="small" sx={{ mt: 1, '& td': { fontSize: 11 } }}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {x.dataRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="caption" color="text.secondary">
                    None
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              x.dataRequests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell sx={{ maxWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.message}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </>
    );
  }

  if (id === 'spine-onboarding') {
    const o = p.onboarding;
    return (
      <>
        <SectionTitle icon={<Layers />} title="Onboarding" />
        <Stack spacing={0}>
          <KVRow label="Phase completed" value={fmt(o.completed)} />
          <KVRow label="Services added" value={`${o.services.count} (${fmt(o.services.added)})`} />
          <KVRow label="Availability set" value={fmt(o.availability.set)} />
          <KVRow label="Availability type" value={o.availability.type} />
          <KVRow label="Slots" value={o.availability.slotsCount} />
          <KVRow label="Subscription active" value={fmt(o.subscription.active)} />
          <KVRow label="Plan (EN)" value={o.subscription.categoryNameEn} />
          <KVRow label="Plan (AR)" value={o.subscription.categoryNameAr} />
          <KVRow label="Small tasks subscribed" value={fmt(o.smallTaskTypes.subscribed)} />
          <KVRow label="Small task types" value={o.smallTaskTypes.count} />
        </Stack>
      </>
    );
  }

  if (id === 'spine-portfolio') {
    const x = p.portfolio;
    return (
      <>
        <SectionTitle icon={<ViewModule />} title="Portfolio" />
        <Stack spacing={0}>
          <KVRow label="Created" value={fmt(x.created)} />
          <KVRow label="Public" value={fmt(x.public)} />
          <KVRow label="Past projects count" value={x.pastProjectsCount} />
        </Stack>
      </>
    );
  }

  if (id === 'spine-terminal') {
    const suspended = String(j.currentPhase).toUpperCase() === 'SUSPENDED';
    return (
      <>
        <SectionTitle icon={<Flag />} title={suspended ? 'Account status' : 'Completion'} />
        <Stack spacing={0}>
          <KVRow label="Current phase" value={j.currentPhase} />
          <KVRow label="Account status" value={j.status} />
          <KVRow label="Overall progress" value={`${j.overallProgress}%`} />
        </Stack>
      </>
    );
  }

  if (id === 'pillar-services') {
    const x = p.onboarding.services;
    return (
      <>
        <SectionTitle icon={<Layers />} title="Services" />
        <Stack spacing={0}>
          <KVRow label="Added" value={fmt(x.added)} />
          <KVRow label="Count" value={x.count} />
        </Stack>
        <Typography variant="overline" fontWeight={700} sx={{ mt: 2, display: 'block' }}>
          Service list
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
          {x.list.map((s) => (
            <Chip key={s.id} size="small" label={`${s.nameEn} · ${s.nameAr}`} />
          ))}
          {x.list.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </Stack>
      </>
    );
  }

  if (id === 'pillar-availability') {
    const x = p.onboarding.availability;
    return (
      <>
        <SectionTitle icon={<Analytics />} title="Availability" />
        <Stack spacing={0}>
          <KVRow label="Set" value={fmt(x.set)} />
          <KVRow label="Type" value={x.type} />
          <KVRow label="Slots count" value={x.slotsCount} />
        </Stack>
        {x.slots.length > 0 && (
          <>
            <Typography variant="overline" fontWeight={700} sx={{ mt: 2, display: 'block' }}>
              Slots
            </Typography>
            <Table size="small" sx={{ mt: 1, '& td': { fontSize: 11 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {x.slots.map((sl, i) => (
                  <TableRow key={i}>
                    <TableCell>{sl.dayOfWeek}</TableCell>
                    <TableCell>{sl.startTime}</TableCell>
                    <TableCell>{sl.endTime}</TableCell>
                    <TableCell>{fmt(sl.isActive)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </>
    );
  }

  if (id === 'pillar-subscription') {
    const x = p.onboarding.subscription;
    return (
      <>
        <SectionTitle icon={<AssignmentTurnedIn />} title="Subscription" />
        <Stack spacing={0}>
          <KVRow label="Active" value={fmt(x.active)} />
          <KVRow label="Category (EN)" value={x.categoryNameEn} />
          <KVRow label="Category (AR)" value={x.categoryNameAr} />
          <KVRow label="Start" value={x.startDate} />
          <KVRow label="End" value={x.endDate} />
        </Stack>
      </>
    );
  }

  if (id === 'pillar-smalltasks') {
    const x = p.onboarding.smallTaskTypes;
    return (
      <>
        <SectionTitle icon={<Layers />} title="Small tasks" />
        <Stack spacing={0}>
          <KVRow label="Subscribed" value={fmt(x.subscribed)} />
          <KVRow label="Count" value={x.count} />
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
          {x.list.map((s) => (
            <Chip key={s.id} size="small" label={`${s.nameEn} · ${s.nameAr}`} />
          ))}
        </Stack>
      </>
    );
  }

  if (node.type === 'journeyPhase') {
    const d = node.data as JourneyPhaseNodeData;
    return (
      <>
        <SectionTitle icon={<Flag />} title={d.label} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.labelAr}
        </Typography>
        <Stack spacing={0}>
          <KVRow label="Completed" value={fmt(d.completed)} />
          <KVRow label="Active step" value={fmt(d.active)} />
          <KVRow label="Subtitle" value={d.subtitle?.replace(/\n/g, ' · ') || '—'} />
        </Stack>
      </>
    );
  }

  if (node.type === 'journeyPillar') {
    const d = node.data as JourneyPillarNodeData;
    return (
      <>
        <SectionTitle icon={<Layers />} title={d.label} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.labelAr}
        </Typography>
        <Stack spacing={0}>
          <KVRow label="Completed" value={fmt(d.completed)} />
          <KVRow label="Detail" value={d.detail} />
        </Stack>
      </>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        No extended inspector for this node. Raw graph id: <code>{node.id}</code>
      </Typography>
    </>
  );
}

function CustomerInspectorBody({ node, c }: { node: Node; c: CustomerJourney }) {
  const id = node.id;

  if (id === 'cust-hub') {
    const d = node.data as JourneyHubNodeData;
    return (
      <>
        <SectionTitle icon={<Hub />} title="Customer profile" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.titleAr}
        </Typography>
        <Stack spacing={0}>
          <KVRow label="Customer ID" value={c.customerId} />
          <KVRow label="Name" value={c.name} />
          <KVRow label="Phone" value={c.phoneNumber} />
          <KVRow label="Email" value={c.email} />
          <KVRow label="Status" value={<Chip size="small" label={c.status} />} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Card summary
        </Typography>
        <Box component="ul" className="uj-inspector-bullets" sx={{ m: 0, pl: 2, mt: 0.5 }}>
          {d.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}>
                {line}
              </Typography>
            </li>
          ))}
        </Box>
      </>
    );
  }

  if (id === 'cust-reg') {
    return (
      <>
        <SectionTitle icon={<Flag />} title="Registration & terms" />
        <Stack spacing={0}>
          <KVRow label="Registration date" value={c.registrationDate} />
          <KVRow label="Terms accepted at" value={c.acceptedTermsAt} />
        </Stack>
      </>
    );
  }

  if (id === 'cust-activity') {
    return (
      <>
        <SectionTitle icon={<Analytics />} title="Login & project activity" />
        <Stack spacing={0}>
          <KVRow label="Last login" value={c.lastLoginDate} />
          <KVRow label="Last project posted" value={c.lastProjectPostedDate} />
        </Stack>
      </>
    );
  }

  if (id === 'cust-projects') {
    return (
      <>
        <SectionTitle icon={<TrendingUp />} title="Projects posted" />
        <Stack spacing={0}>
          <KVRow label="Total posted" value={c.totalProjectsPosted} />
          <KVRow label="Last 30 days" value={c.projectsLast30Days} />
          <KVRow label="Last 60 days" value={c.projectsLast60Days} />
        </Stack>
      </>
    );
  }

  if (id === 'cust-quotes') {
    const q = c.quoteInteractions;
    return (
      <>
        <SectionTitle icon={<ShowChart />} title="Quote interactions" />
        <Stack spacing={0}>
          <KVRow label="Bids received" value={q.totalBidsReceived} />
          <KVRow label="Accepted" value={q.acceptedBids} />
          <KVRow label="Rejected" value={q.rejectedBids} />
          <KVRow label="Pending" value={q.pendingBids} />
        </Stack>
      </>
    );
  }

  const d = node.data as JourneyMetricNodeData | JourneyPhaseNodeData;
  if ('lines' in d && Array.isArray(d.lines)) {
    return (
      <>
        <SectionTitle icon={<Layers />} title={(d as JourneyMetricNodeData).label} />
        <Box component="ul" className="uj-inspector-bullets" sx={{ m: 0, pl: 2 }}>
          {(d as JourneyMetricNodeData).lines.map((line, i) => (
            <li key={i}>
              <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                {line}
              </Typography>
            </li>
          ))}
        </Box>
      </>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Node <code>{node.id}</code>
    </Typography>
  );
}

export type JourneyGraphInspectorProps = {
  mode: JourneyInspectorMode;
  selectedNode: Node | null;
  technician: TechnicianJourney | null;
  customer: CustomerJourney | null;
};

export function JourneyGraphInspector({ mode, selectedNode, technician, customer }: JourneyGraphInspectorProps) {
  if (!selectedNode) {
    return <EmptyHint />;
  }

  return (
    <Box className="uj-inspector-body">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.3 }}>
          {selectedNode.type === 'journeyHub'
            ? (selectedNode.data as JourneyHubNodeData).title
            : selectedNode.type === 'journeyMetric'
              ? (selectedNode.data as JourneyMetricNodeData).label
              : selectedNode.type === 'journeyPhase'
                ? (selectedNode.data as JourneyPhaseNodeData).label
                : selectedNode.type === 'journeyPillar'
                  ? (selectedNode.data as JourneyPillarNodeData).label
                  : 'Node details'}
        </Typography>
        {typeChip(selectedNode)}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontFamily: 'var(--font-mono, monospace)' }}>
        id: {selectedNode.id}
      </Typography>

      {mode === 'technician' && technician ? (
        <TechnicianInspectorBody node={selectedNode} j={technician} />
      ) : mode === 'customer' && customer ? (
        <CustomerInspectorBody node={selectedNode} c={customer} />
      ) : (
        <Typography color="text.secondary" variant="body2">
          No journey loaded.
        </Typography>
      )}
    </Box>
  );
}

export function JourneyUserSummaryCard({
  mode,
  technician,
  customer,
}: {
  mode: JourneyInspectorMode;
  technician: TechnicianJourney | null;
  customer: CustomerJourney | null;
}) {
  if (mode === 'technician' && technician) {
    const j = technician;
    return (
      <Box className="uj-detail-hero">
        <Box className="uj-detail-hero__avatar">
          <AccountCircle sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap title={j.name}>
            {j.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            #{j.technicianId} · {j.phoneNumber}
          </Typography>
          {j.email && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap title={j.email}>
              {j.email}
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            <Chip size="small" label={j.status} color="primary" variant="outlined" />
            <Chip size="small" label={j.currentPhase} variant="outlined" />
            <Chip
              size="small"
              label={j.isOnline ? 'Online' : 'Offline'}
              color={j.isOnline ? 'success' : 'default'}
              variant={j.isOnline ? 'filled' : 'outlined'}
            />
          </Stack>
        </Box>
      </Box>
    );
  }

  if (mode === 'customer' && customer) {
    const c = customer;
    return (
      <Box className="uj-detail-hero">
        <Box className="uj-detail-hero__avatar">
          <Person sx={{ fontSize: 36, color: 'primary.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap title={c.name}>
            {c.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            #{c.customerId} · {c.phoneNumber}
          </Typography>
          {c.email && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {c.email}
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            <Chip size="small" label={c.status} color="primary" variant="outlined" />
            <Chip size="small" label={`${c.totalProjectsPosted} projects`} variant="outlined" />
          </Stack>
        </Box>
      </Box>
    );
  }

  return null;
}
